import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import MacroList from './MacroList';
import MacroModal from './MacroModal';
import DeleteConfirmationDialog from './DeleteConfirmationDialog';
import useMacros from '../hooks/useMacros';
import useLanguages from '../hooks/useLanguages';
import useMacroActions from '../hooks/useMacroActions';
import { useToast } from '../../../Context/ToastContext';

// Import custom icon components
import { 
  DocumentIcon, ChartIcon, MoneyIcon, SearchIcon, 
  TextIcon, ListIcon, BriefcaseIcon, ChecklistIcon,
  PinIcon, BellIcon, GearIcon, ChartUpIcon, ChartDownIcon,
  KeyIcon, CalculatorIcon, AlarmIcon, LightbulbIcon,
  FolderIcon, FolderOpenIcon, MailIcon, PhoneIcon, MessageIcon,
  UserIcon, UsersIcon, HomeIcon, GlobeIcon, PackageIcon,
  ShoppingCartIcon, CreditCardIcon, BarChartIcon, PieChartIcon,
  ActivityIcon, AwardIcon, BookmarkIcon, BookIcon, TargetIcon,
  TagIcon, StarIcon, HeartIcon, ThumbsUpIcon, EditIcon,
  TrashIcon, CopyIcon, ClipboardIcon, DownloadIcon, UploadIcon,
  SendIcon, ImageIcon, VideoIcon, MusicIcon, CameraIcon,
  MonitorIcon, SmartphoneIcon, ServerIcon, DatabaseIcon
} from './IconComponents';

const Container = styled.div`
  padding: 15px;
  color: rgba(255, 255, 255, 0.9);
  height: 100%;
  overflow-y: auto;
  position: relative;
  /* Ensure dropdowns and absolute positioned elements stay within bounds */
  overflow-x: hidden;
  /* Create stacking context to contain z-indexed elements */
  isolation: isolate;
  /* Ensure container width constraints */
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;
`;

const SectionTitle = styled.h3`
  font-size: 16px;
  margin: 30px 0 15px;
  color: rgba(255, 255, 255, 0.9);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

// Local storage keys
const STORAGE_KEYS = {
  CUSTOM_MACROS: 'maven_custom_macros',
  CUSTOMIZED_PREDEFINED_MACROS: 'maven_customized_predefined_macros'
};

/**
 * Container component for macro management
 * Handles layout and state management for macros
 */
const MacroContainer = ({ sectionTitle }) => {
  const [showMacroModal, setShowMacroModal] = useState(false);
  const [editingMacro, setEditingMacro] = useState(null);
  const [isEditingPredefined, setIsEditingPredefined] = useState(false);
  const [predefinedMacroType, setPredefinedMacroType] = useState(null);
  const { showToast } = useToast();
  
  // Delete confirmation dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [macroToDelete, setMacroToDelete] = useState(null);
  const [deleteType, setDeleteType] = useState(null); // 'custom' or 'predefined'
  
  // Get macro actions
  const {
    sendToChatbot,
    createDirectiveWithConversation
  } = useMacroActions();

  // Get language hooks
  const {
    languages,
    selectedLanguage,
    showLanguageDropdown,
    setShowLanguageDropdown,
    handleLanguageSelect,
    handleRightClick: handleLanguageRightClick
  } = useLanguages();

  // State for custom macros 
  const [customMacros, setCustomMacros] = useState([]);
  
  // State for storing customized versions of predefined macros
  const [customizedPredefinedMacros, setCustomizedPredefinedMacros] = useState({
    income: null,
    balance: null
  });
  
  // Map of icon types to icon components
  const iconComponentsMap = {
    translate: GlobeIcon,
    income: MoneyIcon,
    balance: ChartIcon,
    document: DocumentIcon,
    chart: ChartIcon,
    money: MoneyIcon,
    search: SearchIcon,
    text: TextIcon,
    list: ListIcon,
    briefcase: BriefcaseIcon,
    checklist: ChecklistIcon,
    pin: PinIcon,
    bell: BellIcon,
    gear: GearIcon,
    chartUp: ChartUpIcon,
    chartDown: ChartDownIcon,
    key: KeyIcon,
    calculator: CalculatorIcon,
    alarm: AlarmIcon,
    lightbulb: LightbulbIcon,
    folder: FolderIcon,
    folderOpen: FolderOpenIcon,
    mail: MailIcon,
    phone: PhoneIcon,
    message: MessageIcon,
    user: UserIcon,
    users: UsersIcon,
    home: HomeIcon,
    globe: GlobeIcon,
    package: PackageIcon,
    shoppingCart: ShoppingCartIcon,
    creditCard: CreditCardIcon,
    barChart: BarChartIcon,
    pieChart: PieChartIcon,
    activity: ActivityIcon,
    award: AwardIcon,
    bookmark: BookmarkIcon,
    book: BookIcon,
    target: TargetIcon,
    tag: TagIcon,
    star: StarIcon,
    heart: HeartIcon,
    thumbsUp: ThumbsUpIcon,
    edit: EditIcon,
    trash: TrashIcon,
    copy: CopyIcon,
    clipboard: ClipboardIcon,
    download: DownloadIcon,
    upload: UploadIcon,
    send: SendIcon,
    image: ImageIcon,
    video: VideoIcon,
    music: MusicIcon,
    camera: CameraIcon,
    monitor: MonitorIcon,
    smartphone: SmartphoneIcon,
    server: ServerIcon,
    database: DatabaseIcon
  };
  
  // Get icon component from type
  const getIconComponent = (iconType) => {
    const IconComponent = iconComponentsMap[iconType];
    return IconComponent ? <IconComponent /> : null;
  };
  
  // Function to prepare macro data for localStorage by converting React components to type strings
  const prepareForStorage = (macros) => {
    if (Array.isArray(macros)) {
      // For array of custom macros
      return macros.map(macro => ({
        ...macro,
        customIcon: null, // We don't store the React component
        iconType: macro.iconType // We store the type string instead
      }));
    } else {
      // For customized predefined macros object
      const result = {};
      for (const key in macros) {
        if (macros[key]) {
          result[key] = {
            ...macros[key],
            customIcon: null, // We don't store the React component
            iconType: macros[key].iconType || key // Use provided iconType or default to key
          };
        } else {
          result[key] = null;
        }
      }
      return result;
    }
  };
  
  // Function to hydrate macro data from localStorage by converting type strings to React components
  const hydrateFromStorage = (data, isPredefined = false) => {
    if (!data) return isPredefined ? {} : [];
    
    if (Array.isArray(data)) {
      // For array of custom macros
      return data.map(macro => ({
        ...macro,
        customIcon: macro.iconType ? getIconComponent(macro.iconType) : null
      }));
    } else {
      // For customized predefined macros object
      const result = {};
      for (const key in data) {
        if (data[key]) {
          result[key] = {
            ...data[key],
            customIcon: data[key].iconType ? getIconComponent(data[key].iconType) : null
          };
        } else {
          result[key] = null;
        }
      }
      return result;
    }
  };
  
  // Load macros from localStorage on component mount
  useEffect(() => {
    try {
      // Load custom macros
      const savedCustomMacros = localStorage.getItem(STORAGE_KEYS.CUSTOM_MACROS);
      if (savedCustomMacros) {
        const parsedMacros = JSON.parse(savedCustomMacros);
        setCustomMacros(hydrateFromStorage(parsedMacros));
      }
      
      // Load customized predefined macros
      const savedPredefinedMacros = localStorage.getItem(STORAGE_KEYS.CUSTOMIZED_PREDEFINED_MACROS);
      if (savedPredefinedMacros) {
        const parsedPredefinedMacros = JSON.parse(savedPredefinedMacros);
        setCustomizedPredefinedMacros(hydrateFromStorage(parsedPredefinedMacros, true));
      }
    } catch (error) {
      console.error('Error loading macros from localStorage:', error);
    }
  }, []);
  
  // Save custom macros to localStorage whenever they change
  useEffect(() => {
    try {
      const macrosForStorage = prepareForStorage(customMacros);
      localStorage.setItem(STORAGE_KEYS.CUSTOM_MACROS, JSON.stringify(macrosForStorage));
    } catch (error) {
      console.error('Error saving custom macros to localStorage:', error);
    }
  }, [customMacros]);
  
  // Save customized predefined macros to localStorage whenever they change
  useEffect(() => {
    try {
      const macrosForStorage = prepareForStorage(customizedPredefinedMacros);
      localStorage.setItem(STORAGE_KEYS.CUSTOMIZED_PREDEFINED_MACROS, JSON.stringify(macrosForStorage));
    } catch (error) {
      console.error('Error saving customized predefined macros to localStorage:', error);
    }
  }, [customizedPredefinedMacros]);
  
  // Define predefined macros with handlers
  const basePredefinedMacros = {
    translate: {
      icon: '🌐',
      name: 'Translate Conversation',
      customIcon: getIconComponent('translate'),
      iconType: 'translate',
      onClick: (e) => {
        if (e) e.preventDefault();
        
        // Get language name, accounting for the new structure with region property
        const lang = languages.find(l => l.code === selectedLanguage);
        const langName = lang ? lang.name : 'English';
        const directive = `Translate the following conversation to ${langName}:`;
        const fullDirective = createDirectiveWithConversation(directive);
        if (fullDirective) {
          sendToChatbot(fullDirective);
        }
      },
      onContextMenu: handleLanguageRightClick,
      onEdit: (e) => {
        if (e) e.preventDefault();
        setShowLanguageDropdown(true);
      },
      directive: 'Translate the conversation to the selected language'
    },
    income: {
      icon: '💰',
      name: 'Find Incomes in Conversation',
      customIcon: getIconComponent('income'),
      iconType: 'income',
      onClick: (e) => {
        if (e) e.preventDefault();
        const directive = customizedPredefinedMacros.income?.directive || 
                         'Find all the incomes that the user mentioned during this conversation.';
        const fullDirective = createDirectiveWithConversation(directive);
        if (fullDirective) {
          sendToChatbot(fullDirective);
        }
      },
      onContextMenu: (e) => {
        e.preventDefault();
      },
      directive: 'Find all the incomes that the user mentioned during this conversation.'
    },
    balance: {
      icon: '💼',
      name: 'Calculate Balance',
      customIcon: getIconComponent('balance'),
      iconType: 'balance',
      onClick: (e) => {
        if (e) e.preventDefault();
        const directive = customizedPredefinedMacros.balance?.directive || 
                         'Find all income and expense transactions mentioned in the conversation, list them chronologically, and calculate the total balance (income minus expenses).';
        const fullDirective = createDirectiveWithConversation(directive);
        if (fullDirective) {
          sendToChatbot(fullDirective);
        }
      },
      onContextMenu: (e) => {
        e.preventDefault();
      },
      directive: 'Find all income and expense transactions mentioned in the conversation, list them chronologically, and calculate the total balance (income minus expenses).'
    },
    custom: {
      onClick: (macro) => {
        const directive = macro.directive || 'Analyze the conversation';
        
        // Check if this macro should include the full conversation
        let fullDirective;
        if (macro.includeConversation !== false) {
          fullDirective = createDirectiveWithConversation(directive);
        } else {
          fullDirective = directive;
        }
        
        if (fullDirective) {
          sendToChatbot(fullDirective);
        }
      }
    }
  };

  // Combine base predefined macros with any customized versions, excluding deleted ones
  const predefinedMacros = useMemo(() => {
    const macros = {
      // Always include translate and custom (they can't be deleted)
      translate: basePredefinedMacros.translate,
      custom: basePredefinedMacros.custom
    };
    
    // Only add income if not deleted
    if (!customizedPredefinedMacros.income?.deleted) {
      macros.income = {
        ...basePredefinedMacros.income,
        ...(customizedPredefinedMacros.income || {})
      };
    }
    
    // Only add balance if not deleted
    if (!customizedPredefinedMacros.balance?.deleted) {
      macros.balance = {
        ...basePredefinedMacros.balance,
        ...(customizedPredefinedMacros.balance || {})
      };
    }
    
    return macros;
  }, [customizedPredefinedMacros, selectedLanguage, languages, sendToChatbot, createDirectiveWithConversation, setShowLanguageDropdown, handleLanguageRightClick]);

  // Handle adding a new macro
  const handleAddMacroClick = () => {
    setEditingMacro(null);
    setIsEditingPredefined(false);
    setPredefinedMacroType(null);
    setShowMacroModal(true);
  };
  
  // Handle editing a custom macro
  const handleEditMacro = (macro, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    setEditingMacro({
      ...macro,
      prompt: macro.directive,
      iconType: macro.iconType
    });
    setIsEditingPredefined(false);
    setPredefinedMacroType(null);
    setShowMacroModal(true);
  };
  
  // Handle editing a predefined macro
  const handlePredefinedMacroEdit = (type, macro, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    // Get current values, either customized or base values
    const currentMacro = customizedPredefinedMacros[type] || macro;
    
    setEditingMacro({
      name: currentMacro.name,
      icon: currentMacro.icon,
      prompt: currentMacro.directive,
      includeConversation: true, // Predefined macros always include conversation
      iconType: currentMacro.iconType || type
    });
    setIsEditingPredefined(true);
    setPredefinedMacroType(type);
    setShowMacroModal(true);
  };
  
  // Handle deleting a predefined macro
  const handlePredefinedMacroDelete = (type, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    // Get the macro name for the confirmation dialog
    const macroName = basePredefinedMacros[type]?.name || type;
    
    setMacroToDelete({ type, name: macroName });
    setDeleteType('predefined');
    setShowDeleteDialog(true);
  };

  // Handle saving a new or edited macro
  const handleSaveMacro = (macroData) => {
    if (isEditingPredefined && predefinedMacroType) {
      // Saving changes to a predefined macro
      setCustomizedPredefinedMacros(prev => ({
        ...prev,
        [predefinedMacroType]: {
          name: macroData.name,
          icon: macroData.icon,
          directive: macroData.prompt,
          iconType: macroData.iconType || predefinedMacroType,
          customIcon: macroData.iconType ? getIconComponent(macroData.iconType) : null
        }
      }));
    } else if (macroData.id) {
      // Editing existing custom macro
      setCustomMacros(prevMacros => 
        prevMacros.map(macro => 
          macro.id === macroData.id ? { 
            ...macro, 
            ...macroData,
            directive: macroData.prompt,
            includeConversation: macroData.includeConversation,
            iconType: macroData.iconType,
            customIcon: macroData.iconType ? getIconComponent(macroData.iconType) : null
          } : macro
        )
      );
    } else {
      // Creating new custom macro
      const newMacro = {
        ...macroData,
        id: Date.now().toString(),
        directive: macroData.prompt,
        includeConversation: macroData.includeConversation,
        iconType: macroData.iconType,
        customIcon: macroData.iconType ? getIconComponent(macroData.iconType) : null
      };
      setCustomMacros(prevMacros => [...prevMacros, newMacro]);
    }
    
    // Reset editing state
    setIsEditingPredefined(false);
    setPredefinedMacroType(null);
    setShowMacroModal(false);
  };

  // Handle deleting a custom macro
  const handleDeleteCustomMacro = (id, e) => {
    if (e) {
      e.stopPropagation();
      e.preventDefault();
    }
    
    // Find the macro to get its name
    const macro = customMacros.find(m => m.id === id);
    const macroName = macro?.name || 'Unknown';
    
    setMacroToDelete({ id, name: macroName });
    setDeleteType('custom');
    setShowDeleteDialog(true);
  };

  // Handle confirmed deletion
  const handleConfirmDelete = () => {
    if (!macroToDelete || !deleteType) return;
    
    if (deleteType === 'custom') {
      // Delete custom macro
      setCustomMacros(prevMacros => prevMacros.filter(macro => macro.id !== macroToDelete.id));
    } else if (deleteType === 'predefined') {
      // Delete predefined macro (remove from customized list and hide from UI)
      setCustomizedPredefinedMacros(prev => ({
        ...prev,
        [macroToDelete.type]: { deleted: true }
      }));
    }
    
    // Reset state and close dialog
    setMacroToDelete(null);
    setDeleteType(null);
    setShowDeleteDialog(false);
  };

  return (
    <Container>
      {sectionTitle && <SectionTitle>{sectionTitle}</SectionTitle>}
      
      <MacroList
        predefinedMacros={predefinedMacros}
        customMacros={customMacros || []}
        languages={languages || []}
        showLanguageDropdown={showLanguageDropdown || false}
        selectedLanguage={selectedLanguage || 'en'}
        onLanguageSelect={handleLanguageSelect}
        onLanguageDropdownClose={() => setShowLanguageDropdown(false)}
        onAddMacroClick={handleAddMacroClick}
        onCustomMacroDelete={handleDeleteCustomMacro}
        onCustomMacroEdit={handleEditMacro}
        onPredefinedMacroEdit={handlePredefinedMacroEdit}
        onPredefinedMacroDelete={handlePredefinedMacroDelete}
      />

      {/* Macro Creation/Editing Modal */}
      <MacroModal
        isOpen={showMacroModal}
        onClose={() => setShowMacroModal(false)}
        onSave={handleSaveMacro}
        initialValues={editingMacro}
        iconComponentsMap={iconComponentsMap}
      />

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmationDialog
        isOpen={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        onConfirm={handleConfirmDelete}
        macroName={macroToDelete?.name || ''}
        isPredefined={deleteType === 'predefined'}
      />
    </Container>
  );
};

MacroContainer.propTypes = {
  sectionTitle: PropTypes.string
};

MacroContainer.defaultProps = {
  sectionTitle: null
};

export default MacroContainer; 